import { Box, Button, Grid, Group, Tabs } from '@mantine/core';
import { IconDeviceFloppy } from '@tabler/icons-react';
import queryString from 'query-string';

import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router';
import ErrorMessage from '../../components/ErrorMessage';
import Metadata from '../../components/Metadata';
import SettingsTabTikTok from '../../components/Settings/Tabs/SettingsTabTikTok';
import { Page, SettingsDomainTabs } from '../../constants/pageTitles';
import { TIKTOK_TRACKING_EVENT_NAMES } from '../../constants/tiktokTrackingEvents';
import useFetch from '../../helpers/useFetch';
import { setTiktokAccount as setTiktokAccountRedux } from '../../store/tiktokSlice';

const tabs = ['tiktok'];

// Pure reducer behind handleChange, exported so the NULL-column handling can be
// pinned by tests without standing up a page render.
export const applyTikTokChange = (prev, { name, value }) => {
  if (name === 'pixelId') {
    return { ...prev, pixelCode: value };
  }

  if (name === 'backendTracking') {
    const turningOn = value && !prev?.backendTracking;
    const turningOff = !value && prev?.backendTracking;

    // A no-op toggle must not seed or unwind anything, whatever the caller
    // does: an already-ON or already-OFF click stays a no-op.
    if (!turningOn && !turningOff) {
      return prev;
    }

    if (turningOn && !Array.isArray(prev?.enabledTrackingEvents)) {
      // A null column means "send everything" (legacy). Flipping tracking on
      // materializes that as the explicit full list, so the save writes an
      // array and the permissive state can only persist deliberately.
      // Tagged as seeded so a straight ON->OFF with no other edit can unwind
      // it below, instead of leaving a phantom-dirty form.
      return {
        ...prev,
        backendTracking: value,
        enabledTrackingEvents: [...TIKTOK_TRACKING_EVENT_NAMES],
        _trackingEventsSeeded: true,
      };
    }

    if (turningOff && prev?._trackingEventsSeeded) {
      // Nothing touched the picker since the OFF->ON seed: unwind it back to
      // the pre-toggle null instead of leaving a value the operator never
      // chose sitting in state.
      const { _trackingEventsSeeded, ...rest } = prev;
      return { ...rest, backendTracking: value, enabledTrackingEvents: null };
    }

    return { ...prev, backendTracking: value };
  }

  if (name === 'enabledTrackingEvents' && prev?._trackingEventsSeeded) {
    // A deliberate edit while the seed is in place is a real choice, not the
    // seed anymore - stop tracking it as something to unwind on OFF.
    const { _trackingEventsSeeded, ...rest } = prev;
    return { ...rest, enabledTrackingEvents: value };
  }

  return { ...prev, [name]: value };
};

const SettingTikTokPage = () => {
  const location = useLocation();
  const search = queryString.parse(location.search);
  const currentTikTokAccount = useSelector((state) => state.tiktok.account);
  const [tiktokAccount, setTiktokAccount] = useState(currentTikTokAccount);
  const [_validated, setValidated] = useState();
  const [saving, setSaving] = useState();
  const [error, setError] = useState(search?.error ? { message: search?.error } : undefined);

  const dispatch = useDispatch();
  const fetch = useFetch();

  const { accountId, domainId } = useParams();

  let tab = search.tab || tabs[0];

  const tiktokAccountChanged = JSON.stringify(currentTikTokAccount) !== JSON.stringify(tiktokAccount);

  useEffect(() => {
    setTiktokAccount(currentTikTokAccount);
  }, [currentTikTokAccount]);

  const handleChange = (event) => {
    setTiktokAccount((prev) => applyTikTokChange(prev, event.target));
  };

  const save = async (account) => {
    setError(undefined);
    // _trackingEventsSeeded is bookkeeping for the OFF/ON unwind above, not
    // part of the account shape the API expects.
    const { _trackingEventsSeeded, ...payload } = account || {};
    try {
      const { tiktokAccount } = await fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/tiktok/account`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      dispatch(setTiktokAccountRedux(tiktokAccount));
    } catch (error) {
      setError(error);
    }
  };

  const validateForm = (event) => {
    const form = event.currentTarget;
    if (form.checkValidity() === false) {
      event.preventDefault();
      event.stopPropagation();
      setValidated(true);
      return false;
    }
    setValidated(true);
    return true;
  };

  const submitForm = async (e) => {
    e.preventDefault();
    setError(undefined);

    if (validateForm(e)) {
      try {
        setSaving(true);
        await save(tiktokAccount);
      } catch (error) {
        setError(error);
      } finally {
        setSaving(false);
      }
    }
  };

  const saveButton = (
    <Button type="submit" disabled={!tiktokAccountChanged || !tiktokAccount?.advertiserId || !tiktokAccount?.pixelCode}>
      <IconDeviceFloppy size={14} /> {saving ? 'Saving...' : tiktokAccountChanged ? 'Save' : 'Saved'}
    </Button>
  );

  return (
    <div>
      <Metadata title={`${Page['settings']} | ${SettingsDomainTabs[tab]}`} />

      <Grid pt={30} my={20}>
        <Grid.Col span={12}>
          <Box component="form" ta="left" noValidate onSubmit={(e) => submitForm(e)}>
            <Tabs value={tab} mb="md">
              <Tabs.List>
                <Tabs.Tab value="tiktok">TikTok</Tabs.Tab>
              </Tabs.List>
              <Tabs.Panel value="tiktok">
                <SettingsTabTikTok
                  tiktokAccount={tiktokAccount}
                  setTiktokAccount={setTiktokAccount}
                  handleChange={handleChange}
                />
              </Tabs.Panel>
            </Tabs>
            <Group justify="flex-end" gap="xs" py="md">
              {error && <ErrorMessage message={error.message} />}
              {saveButton}
            </Group>
          </Box>
        </Grid.Col>
      </Grid>
    </div>
  );
};

export default SettingTikTokPage;
